import { UserCheck, Clock, Star } from 'lucide-react';
import useInView from '../hooks/useInView';
import {
  PermanentHiringIllustration,
  ContractStaffingIllustration,
  ExecutiveSearchIllustration,
} from './ServiceIllustration';

const SERVICES = [
  {
    icon: UserCheck,
    illustration: PermanentHiringIllustration,
    title: 'Permanent Hiring',
    subtitle: 'Mid to Senior Roles',
    description: 'Execution-driven hiring for critical full-time roles. We align deeply on the profile, run structured searches, and deliver candidates with insight reports — not just CVs.',
    tags: ['Retained Search', 'Contingency', 'Mid–Senior'],
  },
  {
    icon: Clock,
    illustration: ContractStaffingIllustration,
    title: 'Contract Staffing',
    subtitle: 'Flexible Engagement Models',
    description: 'W2, C2C, and margin-only models to support fast-moving teams. Full payroll management, compliance, and seamless candidate onboarding.',
    tags: ['W2 Staffing', 'Corp-to-Corp', 'Margin-Only'],
  },
  {
    icon: Star,
    illustration: ExecutiveSearchIllustration,
    title: 'Executive Search',
    subtitle: 'Leadership Hiring',
    description: 'Structured, confidential search for C-suite and VP-level leaders. We bring Founder and CXO-level alignment to every leadership mandate.',
    tags: ['C-Suite', 'VP Level', 'Structured Delivery'],
  },
];

export default function ServicesSection() {
  const [ref, inView] = useInView();

  return (
    <section id="services" className="py-24 bg-[#F9FAFB]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="relative mb-14">
          <div className="grid lg:grid-cols-2 gap-8 items-end">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="w-6 h-px bg-[#E8500A]" />
                <span className="text-[#E8500A] text-xs font-semibold uppercase tracking-[0.22em]" style={{ fontFamily: '"Inter",sans-serif' }}>
                  What We Do
                </span>
              </div>
              <h2
                className="text-4xl sm:text-5xl font-black text-[#0A1628] leading-none tracking-tight"
                style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}
                data-testid="services-heading"
              >
                Hiring Solutions,<br />Built for Scale.
              </h2>
            </div>
            <p className="text-[#4B5563] text-base leading-relaxed lg:text-right lg:pb-2" style={{ fontFamily: '"Inter",sans-serif' }}>
              Three focused practice areas. One execution standard.
            </p>
          </div>
        </div>

        {/* Cards */}
        <div ref={ref} className="grid md:grid-cols-3 gap-0 border border-black/8">
          {SERVICES.map((svc, i) => {
            const Illustration = svc.illustration;
            return (
              <div
                key={i}
                className="group card-sweep bg-white border-r last:border-r-0 border-black/8 hover:bg-[#0A1628] transition-all duration-300 cursor-default overflow-hidden"
                style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(16px)', transition: `all 0.55s ease ${i * 60}ms` }}
                data-testid={`service-card-${i}`}
              >
                {/* Animated illustration strip */}
                <Illustration />

                <div className="p-8">
                  <div className="text-xs text-[#E8500A] uppercase tracking-[0.18em] font-semibold mb-2" style={{ fontFamily: '"Inter",sans-serif' }}>
                    {svc.subtitle}
                  </div>
                  <h3
                    className="text-xl font-black text-[#0A1628] group-hover:text-white mb-3 transition-colors duration-300"
                    style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}
                  >
                    {svc.title}
                  </h3>
                  <p className="text-sm text-[#4B5563] group-hover:text-white/60 leading-relaxed mb-6 transition-colors duration-300" style={{ fontFamily: '"Inter",sans-serif' }}>
                    {svc.description}
                  </p>

                  <div className="flex flex-wrap gap-2">
                    {svc.tags.map((tag, j) => (
                      <span
                        key={j}
                        className="text-xs border border-black/10 group-hover:border-white/20 px-3 py-1 text-[#4B5563] group-hover:text-white/50 transition-all duration-300 rounded-sm"
                        style={{ fontFamily: '"Inter",sans-serif' }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
